'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetch } from '@/app/hooks';
import type { ServiceError } from '@/app/types';
import type { ClawBillingMode } from '@/app/types/claw';
import { flattenCoordinatorPhases, type SessionEventBase } from '../../_utils';
import { getSessionCoordinator, sendChat } from '../../apis';
import type { ChatMessageItem, SessionMode, SubtaskPhase } from '../../types';
import { handleDefaultEvent } from './handle-default-event';
import { handleYoloEvent } from './handle-yolo-event';
import type { ChatRequestContent, EventHandlerCtx, StartChatStreamParams } from './types';
import { useSessionEvents } from './use-session-events';

interface UseChatStreamOptions {
  sessionId: string | null | undefined;
  /**
   * 当前 session 的 mode。
   * yolo 模式后端只下发一条 `conversation.message.created`，没有 `turn.completed`，
   * 前端必须主动收尾，否则发送按钮永远不可点。
   */
  sessionMode?: SessionMode | null;
  lastStreamSeq: string | null | undefined;
  onReadStreamSeqChange?: (streamSeq: string) => void;
  /**
   * 由 useChatHistory 从历史推断出的"仍在进行中的 turn"。
   * 命中且 sessionId 一致时，stream hook 会在 SSE 回数据前就把 isStreaming 拉起，
   * 让 UI 立即进入"思考中"，避免必须等到第一帧 SSE 才看到状态。
   */
  runningTurn?: { sessionId: string; turnId: string; awaitingReply?: boolean } | null;
  /**
   * 后端 session.run_phase（'idle' / 'thinking' / ...），来自 state/stream 与 detail 接口。
   * 兜底用：正文已落地、typer 也在跑，但 turn.completed 丢失（典型：刷新续传）导致
   * 流式状态收不掉时，靠它判定"后端这一轮其实已结束"主动收尾。仅 'idle' 触发收尾，
   * 其余值（thinking / unspecified / 空）一律按"仍在进行"对待，避免误杀刚起的流。
   */
  runPhase?: string | null;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageItem[]>>;
  /**
   * sendChat 请求成功返回但 body 中带有 error（如 claw 已失效）时回调。
   * 网络层异常仍走 useFetch 默认 onError 流程。
   */
  onSendApiError?: (error: unknown) => void;
  /**
   * sendChat 失败（接口 4xx/5xx 或网络异常）时调用。
   * - error：接口返回体里的 error，上层据其 reason 区分文案（如审核未过 AuditSubmitIllegal）；网络异常时为 undefined。
   * - 返回 true：上层已接管（移除占位 + 还原草稿等），stream hook 跳过默认的"在 assistant 占位上写错误文案"。
   * - 返回 false / 未实现：走默认 fallback。
   */
  onSendFailed?: (info: { assistantMessageId: string; error?: ServiceError }) => boolean | undefined;
}

export const useChatStream = ({
  sessionId,
  sessionMode,
  lastStreamSeq,
  onReadStreamSeqChange,
  runningTurn,
  runPhase,
  setMessages,
  onSendApiError,
  onSendFailed,
}: UseChatStreamOptions) => {
  const t = useTranslations();
  const failedFallbackMessage = t('claw.chat.error.failed');

  const [isStreaming, setIsStreaming] = useState(false);
  const isStreamingRef = useRef(false);
  const assistantMessageIdRef = useRef<string>('');
  const activeTurnIdRef = useRef<string>('');

  const [phases, setPhases] = useState<SubtaskPhase[]>([]);
  const sessionIdRef = useRef<string | null | undefined>(sessionId);
  // 通过 ref 同步最新 sessionMode，避免 handleSessionEvent 闭包锁定旧值
  const sessionModeRef = useRef<SessionMode | null | undefined>(sessionMode);

  sessionIdRef.current = sessionId;
  sessionModeRef.current = sessionMode;

  // 已结束 turn 内出现过的 tool_call_id：用于丢弃后端在 turn.completed 之后又派发的
  // 迟到 tool.started/completed（典型表现是带新 turn_id 但 tool_call_id 复用），
  // 避免触发 resume 把已完成消息重新置回 thinkingLoading。
  const settledToolCallIdsRef = useRef<Set<string>>(new Set());

  // 本 session 已收尾（turn.completed / turn.failed）的 turn_id。后端偶发复用已结束的
  // turn_id 跑后台 phase-worker（spawn 子 agent），其迟到的 conversation.* 事件会经
  // resume 路径复活已收尾的主气泡。靠这个集合识别"已结束 turn 的迟到事件"并丢弃。
  const completedTurnIdsRef = useRef<Set<string>>(new Set());

  const handleSendErrorRef = useRef<() => void>(() => {});

  const fetchAndSetCoordinatorSnapshot = useCallback(async () => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) return;
    try {
      const { data, error } = await getSessionCoordinator(currentSessionId);
      if (error || !data) return;
      if (sessionIdRef.current !== currentSessionId) return;
      const phases = flattenCoordinatorPhases(data.coordinators ?? []);
      setPhases(phases);
    } catch {
      // 快照拉取失败不影响主流程
    }
  }, []);

  const updateAssistantMessageById = useCallback(
    ({
      assistantMessageId,
      updater,
      createIfMissing = false,
    }: {
      assistantMessageId: string;
      updater: (message: ChatMessageItem) => ChatMessageItem;
      createIfMissing?: boolean;
    }) => {
      if (!assistantMessageId) return;

      setMessages((prev) => {
        const lastIndex = prev.length - 1;

        if (lastIndex >= 0 && prev[lastIndex]?.id === assistantMessageId) {
          const next = [...prev];
          next[lastIndex] = updater(prev[lastIndex]);
          return next;
        }

        const targetIndex = prev.findIndex((m) => m.id === assistantMessageId);
        if (targetIndex < 0) {
          if (!createIfMissing) return prev;
          return [
            ...prev,
            updater({
              id: assistantMessageId,
              role: 'assistant',
              content: '',
              thinkingLoading: false,
            }),
          ];
        }

        const next = [...prev];
        next[targetIndex] = updater(prev[targetIndex]);
        return next;
      });
    },
    [setMessages],
  );

  const updateAssistantMessage = useCallback(
    (updater: (message: ChatMessageItem) => ChatMessageItem) => {
      const id = assistantMessageIdRef.current;
      if (!id) return;
      updateAssistantMessageById({ assistantMessageId: id, updater, createIfMissing: true });
    },
    [updateAssistantMessageById],
  );

  const setAssistantErrorMessage = useCallback(
    (errorMessage: string) => {
      const id = assistantMessageIdRef.current;
      if (!id) return;
      updateAssistantMessageById({
        assistantMessageId: id,
        updater: (message) => {
          if (message.content.trim()) {
            return {
              ...message,
              thinkingLoading: false,
              createdAt: message.createdAt ?? new Date().toISOString(),
            };
          }
          return {
            ...message,
            content: errorMessage,
            thinkingLoading: false,
            createdAt: new Date().toISOString(),
          };
        },
      });
    },
    [updateAssistantMessageById],
  );

  const markStreamCompleted = useCallback(() => {
    // 借 setMessages 的 reducer 形态读取最新快照（return prev 不引发 rerender），
    // 把当前 assistant 消息上已落地的 tool_call_id 全部记入"已结束"集合，
    // 用于拦截 turn.completed 之后的迟到 tool 事件。
    const aid = assistantMessageIdRef.current;
    if (aid) {
      setMessages((prev) => {
        const target = prev.find((m) => m.id === aid);
        target?.tool_calls?.forEach((tc) => {
          if (tc.id) settledToolCallIdsRef.current.add(tc.id);
        });
        return prev;
      });
    }
    // 记下刚收尾的 turn_id：后端复用它跑后台 phase-worker 时，迟到事件靠它识别并丢弃。
    if (activeTurnIdRef.current) {
      completedTurnIdsRef.current.add(activeTurnIdRef.current);
    }
    isStreamingRef.current = false;
    setIsStreaming(false);
    assistantMessageIdRef.current = '';
    activeTurnIdRef.current = '';
  }, [setMessages]);

  // 远端 turn 仍在跑（典型场景：刷新页面后 SSE 续传）但本端没起 turn 时，把流式状态接上：
  // 复用 messages 末尾的 assistant 占位，后续事件直接往这条上 merge；
  // 末尾不是 assistant 时新建一条占位。yolo 路径不会走到这里，已在分流处单独引出。
  const resumeStreamingFromRemoteEvent = useCallback(
    ({ turnId, awaitingReply = false }: { turnId: string; awaitingReply?: boolean }) => {
      if (isStreamingRef.current) return;
      if (!awaitingReply && !turnId) return;

      isStreamingRef.current = true;
      setIsStreaming(true);
      // 等 user 回复后的新 turn：首帧 SSE 才带 turn_id，不能锁死到上一轮
      activeTurnIdRef.current = awaitingReply ? '' : turnId;

      setMessages((prev) => {
        const lastIndex = prev.length - 1;
        const tail = lastIndex >= 0 ? prev[lastIndex] : null;
        if (!awaitingReply && tail && tail.role === 'assistant') {
          assistantMessageIdRef.current = tail.id;
          const next = [...prev];
          next[lastIndex] = { ...tail, thinkingLoading: true };
          return next;
        }
        const placeholderId = awaitingReply ? `assistant-awaiting-${Date.now()}` : `assistant-resume-${turnId}`;
        assistantMessageIdRef.current = placeholderId;
        return [...prev, { id: placeholderId, role: 'assistant', content: '', thinkingLoading: true }];
      });
    },
    [setMessages],
  );

  // 同一份 ctx 在整个 hook 生命周期内引用稳定（字段全是 ref 或 setter / 稳定 callback），
  // handleSessionEvent 不需要每次 render 重建。
  const eventCtxRef = useRef<EventHandlerCtx | null>(null);
  eventCtxRef.current = {
    setMessages,
    setPhases,
    assistantMessageIdRef,
    isStreamingRef,
    activeTurnIdRef,
    settledToolCallIdsRef,
    completedTurnIdsRef,
    updateAssistantMessage,
    setAssistantErrorMessage,
    markStreamCompleted,
    resumeStreamingFromRemoteEvent,
    failedFallbackMessage,
  };

  const handleSessionEvent = useCallback((event: SessionEventBase) => {
    const ctx = eventCtxRef.current;
    if (!ctx) return;
    if (sessionModeRef.current === 'yolo') {
      handleYoloEvent(event, ctx);
    } else {
      handleDefaultEvent(event, ctx);
    }
  }, []);

  useSessionEvents({
    sessionId,
    lastStreamSeq,
    onEvent: handleSessionEvent,
    onStreamSeqAdvance: onReadStreamSeqChange,
    onReconnect: fetchAndSetCoordinatorSnapshot,
  });

  // 业务态在 session 切换 / 退出时的 reset。SSE 订阅生命周期由 useSessionEvents 自管。
  useEffect(() => {
    if (!sessionId || lastStreamSeq === undefined) {
      setPhases([]);
      // 离开 session（点侧边栏回到新建态）时事件订阅已关，turn.completed 不会再到，
      // 必须主动复位 isStreaming，否则发送按钮的 canSend 一直为 false。
      if (!sessionId) {
        markStreamCompleted();
      }
      return;
    }

    setPhases([]);
    settledToolCallIdsRef.current.clear();
    completedTurnIdsRef.current.clear();

    if (lastStreamSeq !== null) {
      fetchAndSetCoordinatorSnapshot();
    }
  }, [sessionId, lastStreamSeq, fetchAndSetCoordinatorSnapshot, markStreamCompleted]);

  useEffect(() => {
    return () => {
      markStreamCompleted();
    };
  }, [markStreamCompleted]);

  // 历史推断出的"远端 turn 进行中"：在 SSE 还没回数据前就把流式状态接上，
  // 让 ChatInput 立刻禁用、思考态立刻显示。仅当 runningTurn 与当前 sessionId 匹配时生效。
  useEffect(() => {
    if (!runningTurn || !sessionId || runningTurn.sessionId !== sessionId) return;
    resumeStreamingFromRemoteEvent({
      turnId: runningTurn.turnId,
      awaitingReply: runningTurn.awaitingReply,
    });
  }, [runningTurn, sessionId, resumeStreamingFromRemoteEvent]);

  // run_phase 兜底收尾：当本端仍在流式态、且当前 assistant 已经产出过内容，
  // 但后端 session.run_phase 已转为 'idle'，说明这一轮其实已经结束（典型：刷新续传
  // 命中 resume 把状态接上，但收尾的 turn.completed 不会再来）。此时主动把卡住的
  // "思考中"收掉。依赖 [runPhase, isStreaming]：既覆盖 run_phase thinking→idle，
  // 也覆盖 resume 后 isStreaming 边沿到来时 run_phase 已是 idle 的情况。
  // 只认 'idle'；'已有产出' 这道闸挡住"刚发送、后端还没起 turn、run_phase 仍是旧 idle 值"
  // 的窗口，避免误杀刚起的流。typer 是否播完不在判定内（收尾不打断 MarkdownTyper）。
  useEffect(() => {
    if (runPhase !== 'idle') return;
    if (!isStreamingRef.current) return;

    const aid = assistantMessageIdRef.current;
    let hasProducedOutput = false;
    setMessages((prev) => {
      const target = aid ? prev.find((m) => m.id === aid) : [...prev].reverse().find((m) => m.role === 'assistant');
      hasProducedOutput = !!(
        target &&
        (target.content?.trim() ||
          target.thinking?.trim() ||
          target.tool_calls?.length ||
          target.fileList?.length ||
          target.story)
      );
      // 仅在确实要收尾且消息仍处于 loading 时才改写，否则保持原引用不触发 rerender
      if (!target || !hasProducedOutput || !target.thinkingLoading) return prev;
      return prev.map((m) => (m.id === target.id ? { ...m, thinkingLoading: false } : m));
    });

    if (!hasProducedOutput) return;
    markStreamCompleted();
  }, [runPhase, isStreaming, markStreamCompleted, setMessages]);

  const { runAsync: runChatAsync } = useFetch(
    (payload: {
      session_id: string;
      msg: { role: 'user'; contents: ChatRequestContent[] };
      model_version?: string;
      resolution?: string;
      billing_mode?: ClawBillingMode;
    }) => sendChat(payload),
    { manual: true, onError: () => handleSendErrorRef.current() },
  );

  handleSendErrorRef.current = () => {
    setAssistantErrorMessage(failedFallbackMessage);
    markStreamCompleted();
  };

  const startChatStream = useCallback(
    ({ assistantMessageId, payload }: StartChatStreamParams) => {
      assistantMessageIdRef.current = assistantMessageId;
      activeTurnIdRef.current = '';
      isStreamingRef.current = true;
      setIsStreaming(true);

      updateAssistantMessageById({
        assistantMessageId,
        createIfMissing: true,
        updater: (message) => ({
          ...message,
          thinkingLoading: true,
        }),
      });

      runChatAsync(payload)
        .then((result) => {
          // useFetch 在接口返回 4xx/5xx 时仍把 { data, error } 放在 resolved 值里，
          // 不会自动走 onError；这里捕获后通知上层做 inactive 等统一处理。
          const apiError = result?.error ?? undefined;
          if (apiError) {
            onSendApiError?.(apiError);
            const handled = onSendFailed?.({ assistantMessageId, error: apiError });
            if (handled) {
              markStreamCompleted();
            } else {
              handleSendErrorRef.current();
            }
            return;
          }

          // 回填刚发出的 user 消息的 serverMessageId：sendChat 返回的 id 即后端 user msg.id，
          // 分享时需要它。SSE 流没有 user msg_id，且 user 的 stream_seq=0 也不能靠拉历史匹配。
          const userMessageId = result.data?.id;
          if (!userMessageId) return;
          setMessages((prev) => {
            const assistantIndex = prev.findIndex((m) => m.id === assistantMessageId);
            if (assistantIndex <= 0) return prev;
            const userMessage = prev[assistantIndex - 1];
            if (!userMessage || userMessage.role !== 'user' || userMessage.serverMessageId) return prev;
            const next = [...prev];
            next[assistantIndex - 1] = { ...userMessage, serverMessageId: userMessageId };
            return next;
          });
        })
        .catch(() => {
          const handled = onSendFailed?.({ assistantMessageId });
          if (handled) {
            markStreamCompleted();
          } else {
            handleSendErrorRef.current();
          }
        });
    },
    [runChatAsync, updateAssistantMessageById, onSendApiError, onSendFailed],
  );

  return {
    isStreaming,
    isStreamingRef,
    assistantMessageIdRef,
    startChatStream,
    phases,
  };
};
